import { Injectable } from '@angular/core';
import type {
  EditGroupActivity,
  EditGroupMember,
  SportOption,
  SportTypeConfig
} from '../../presentations/models/activities-management.model';
import {
  getSportConfig as getManagementSportConfig,
  normalizeSportValue
} from '../../presentations/utils/management-sport.util';
import { formatDurationFromNumber, formatTime, formatWeekday } from '../../presentations/utils/date.util';

@Injectable({ providedIn: 'root' })
export class ActivityPreviewStateService {
  getCoverPhotoSrc(activity: EditGroupActivity | null, defaultCoverPhotoUrl: string): string {
    const thumbnailUrl = activity?.thumbnailUrl?.trim();
    if (
      !thumbnailUrl ||
      thumbnailUrl === defaultCoverPhotoUrl ||
      thumbnailUrl.includes('group-cover-default') ||
      thumbnailUrl.includes('placeholder')
    ) {
      return defaultCoverPhotoUrl;
    }

    return thumbnailUrl;
  }

  getCreatedOn(activity: EditGroupActivity | null): string {
    const createdAt = activity?.createdAtIso ? new Date(activity.createdAtIso) : null;
    return createdAt && !Number.isNaN(createdAt.getTime())
      ? createdAt.toLocaleDateString('en-GB')
      : activity?.date ?? '-';
  }

  getScheduleLabel(activity: EditGroupActivity | null): string {
    if (!activity?.startDateIso) {
      return '-';
    }

    const weekday = formatWeekday(activity.startDateIso);
    const startTime = formatTime(activity.startDateIso);
    if (!weekday || !startTime) {
      return '-';
    }

    const prefix = activity.recurrence === 'weekly' ? 'Every ' : '';
    return `${prefix}${weekday} at ${startTime}`;
  }

  getStartDate(activity: EditGroupActivity | null): string {
    return this.formatDate(activity?.startDateIso, activity?.date ?? '-');
  }

  getEndDate(activity: EditGroupActivity | null): string {
    return this.formatDate(activity?.endDateIso, '-');
  }

  getDurationLabel(activity: EditGroupActivity | null): string {
    return formatDurationFromNumber(activity?.durationSeconds) || '-';
  }

  getHostEmail(activity: EditGroupActivity | null): string {
    return activity?.hostEmail ?? '-';
  }

  getTypeLabel(activity: EditGroupActivity): string {
    return (activity.activityType ?? '').replace(/\s+-\s+/g, ' • ');
  }

  getLevelLabel(activity: EditGroupActivity, sportTypes: SportTypeConfig[]): string {
    const level = this.getLevelValue(activity.level, activity.sport, sportTypes);
    return level === 'all' ? 'All levels' : `Level ${level}`;
  }

  getLevelOptions(activity: EditGroupActivity | null, sportTypes: SportTypeConfig[]): SportOption[] {
    const maxLevel = this.getSportConfig(activity?.sport ?? '', sportTypes).maxLevel;
    const columnLength = Math.ceil(maxLevel / 2);
    const levelOptions: SportOption[] = [{ label: 'All levels', value: 'all' }];

    if (columnLength + 1 <= maxLevel) {
      levelOptions.push({ label: `Level ${columnLength + 1}`, value: String(columnLength + 1) });
    }

    for (let level = 1; level <= columnLength; level += 1) {
      levelOptions.push({ label: `Level ${level}`, value: String(level) });
      const secondColumnLevel = level + columnLength + 1;
      if (secondColumnLevel <= maxLevel) {
        levelOptions.push({ label: `Level ${secondColumnLevel}`, value: String(secondColumnLevel) });
      }
    }

    return levelOptions;
  }

  getLevelValue(level: string | undefined, sport: string, sportTypes: SportTypeConfig[]): string {
    if (!level) {
      return 'all';
    }

    const normalized = level.trim().toLowerCase();
    if (normalized === 'all' || normalized === 'all levels') {
      return 'all';
    }

    const numericLevel = normalized.match(/\d+/)?.[0] ?? normalized;
    return this.isLevelValueValidForSport(numericLevel, sport, sportTypes) ? numericLevel : 'all';
  }

  isLevelValueValidForSport(level: string, sport: string, sportTypes: SportTypeConfig[]): boolean {
    if (level === 'all') {
      return true;
    }

    const value = Number(level);
    if (!sportTypes.length) {
      return Number.isInteger(value) && value >= 1 && value <= 10;
    }

    return Number.isInteger(value) && value >= 1 && value <= this.getSportConfig(sport, sportTypes).maxLevel;
  }

  getParticipantTotal(activity: EditGroupActivity | null): number {
    return Math.max(activity?.participants ?? 0, 0);
  }

  getVisibleParticipants(activity: EditGroupActivity | null, expanded: boolean, previewCount: number): EditGroupMember[] {
    const participants = this.getActivityParticipants(activity);
    return expanded ? participants : participants.slice(0, previewCount);
  }

  getParticipantsMoreCount(activity: EditGroupActivity | null, previewCount: number): number {
    return Math.max(this.getParticipantTotal(activity) - previewCount, 0);
  }

  canToggleParticipants(activity: EditGroupActivity | null, expanded: boolean, previewCount: number): boolean {
    return expanded || this.getParticipantsMoreCount(activity, previewCount) > 0;
  }

  canSave(
    draft: EditGroupActivity | null,
    original: EditGroupActivity | null,
    level: string,
    originalLevel: string,
    defaultCoverPhotoUrl = ''
  ): boolean {
    if (!draft || !original) {
      return false;
    }

    return (
      !!draft.name?.trim() &&
      this.isRouteUrlValid(draft.routeUrl) &&
      this.hasChanges(draft, original, level, originalLevel, defaultCoverPhotoUrl)
    );
  }

  hasChanges(
    draft: EditGroupActivity | null,
    original: EditGroupActivity | null,
    level: string,
    originalLevel: string,
    defaultCoverPhotoUrl = ''
  ): boolean {
    if (!draft || !original) {
      return false;
    }

    const originalPhotoUrl = this.normalizeCoverPhotoUrl(original.thumbnailUrl, defaultCoverPhotoUrl);
    const draftPhotoUrl = this.normalizeCoverPhotoUrl(draft.thumbnailUrl, defaultCoverPhotoUrl);
    const photoChanged =
      !!draft.backgroundImageFile ||
      draftPhotoUrl !== originalPhotoUrl ||
      (!!draft.backgroundImageRemoved && !!originalPhotoUrl);

    return (
      draft.name !== original.name ||
      draft.description !== original.description ||
      draft.routeUrl !== original.routeUrl ||
      draft.sport !== original.sport ||
      photoChanged ||
      level !== originalLevel
    );
  }

  isRouteUrlValid(routeUrl: string | null | undefined): boolean {
    const value = routeUrl?.trim();
    if (!value) {
      return true;
    }

    if (!/^https?:\/\//i.test(value) || /\s/.test(value)) {
      return false;
    }

    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private normalizeCoverPhotoUrl(photoUrl: string | null | undefined, defaultCoverPhotoUrl: string): string {
    const normalizedUrl = photoUrl?.trim() ?? '';
    if (
      !normalizedUrl ||
      normalizedUrl === defaultCoverPhotoUrl ||
      normalizedUrl.includes('group-cover-default') ||
      normalizedUrl.includes('default_bg') ||
      normalizedUrl.includes('placeholder')
    ) {
      return '';
    }

    return normalizedUrl;
  }

  private getSportConfig(sport: string, sportTypes: SportTypeConfig[]): SportTypeConfig {
    const normalized = normalizeSportValue(sport);
    return getManagementSportConfig(sport, sportTypes) ?? sportTypes[0] ?? {
      label: sport,
      value: normalized,
      icon: normalized,
      maxLevel: 5
    };
  }

  private getActivityParticipants(activity: EditGroupActivity | null): EditGroupMember[] {
    return activity?.participantDetails ?? [];
  }

  private formatDate(value: string | undefined, fallback: string): string {
    if (!value) {
      return fallback;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? fallback
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}
